<template>
  <div>
    <div class="font-bold p-2 flex">Sections</div>
    <div class="flex gap-2 p-2 text-gray-300">
      <SMButton v-tippy="'Duplicate Sections'" icon="copy" :on-click="duplicate" :disabled="eventIsPublished" />
      <SMButton v-tippy="'Delete Sections'" icon="delete" :on-click="remove" />
    </div>
    <ul class="space-y-2 px-2">
      <li class="flex gap-2 items-center h-8">
        <SMIcon icon="visible" />
        <SMCheckbox v-model="visible" v-tippy="'Visible'">Visible</SMCheckbox>
      </li>
      <li v-if="!hasServerSections" class="flex gap-2 items-center h-8">
        <SMIcon icon="standing" />
        <SMCheckbox v-model="ga" v-tippy="'General Admission'" :disabled="hasUnits || eventIsPublished">
          General Admission
        </SMCheckbox>
      </li>
    </ul>
    <ul v-if="!hasServerSections" class="space-y-2 p-2">
      <li class="flex gap-2 items-center">
        <SMIcon icon="category" />
        <SMSelect
          :model-value="categoryId"
          :disabled="eventIsPublished"
          placeholder="Category..."
          @update:modelValue="onCategorySelection"
        >
          <SMOption
            v-for="option in categories"
            :key="option.id"
            :value="option.id"
            :disabled="option.id === 0"
            class="flex gap-2 items-center"
          >
            <div v-if="option.id" class="w-5 h-5 shrink-0" :style="{ background: option.color }" />
            <div class="text-ellipsis grow shrink whitespace-nowrap overflow-hidden">{{ option.name }}</div>
            <div v-if="option.id" class="shrink-0">&nbsp;({{ option.price }})</div>
          </SMOption>
        </SMSelect>
      </li>
      <li class="flex gap-2 px-8 items-center text-xs">
        <span>{{ categoryLabel }}</span>
      </li>
    </ul>
    <ul v-if="!hasServerSections" class="space-y-2 px-2">
      <li>
        <SMInput v-model="level" v-tippy="'Level'" icon="level" :placeholder="levelLabel" />
      </li>
      <li>
        <SMInput v-model="entrance" v-tippy="'Entrance'" icon="entrance" :placeholder="entranceLabel" />
      </li>
    </ul>
    <div class="md-subheading px-2 my-2 bg-gray-200">Styles</div>
    <ul class="space-y-2 p-2">
      <li>
        <SMNumberInput
          v-model="opacity"
          v-tippy="'Opacity'"
          :min="0"
          :max="100"
          icon="opacity"
          @update:modelValue="onOpacityChanged"
        />
      </li>
      <li>
        <div class="flex gap-2 items-center">
          <SMColorPicker
            v-model="fontColor"
            v-tippy="'Label Color'"
            icon="text-color"
            class="grow"
            @update:modelValue="onLabelColorChanged"
          />
        </div>
      </li>
      <li>
        <SMNumberInput
          v-model="fontSize"
          v-tippy="'Font Size'"
          icon="font-size"
          :min="1"
          :max="200"
          @update:modelValue="onFontSizeValueChange"
        />
      </li>
    </ul>
    <SMDialogConfirm
      ref="confirmCategoryAssignmentRef"
      md-title="Confirm Assignment"
      :md-content-html="`
        You are trying to assign a &quot;General Admission&quot; category to a section that contains seats.
        Are you sure you want to continue?
      `"
      md-ok-text="OK"
      md-cancel-text="Cancel"
      @close="categoryAssignmentDialogClosed"
    />
  </div>
</template>
<script lang="ts">
import { EVENTSTATUS, TEXT } from '@/constants';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import eventbus from 'tiny-emitter/instance';
import tinycolor from 'tinycolor2';
import SMIcon from '@/components/ui/SMIcon.vue';
import SMButton from '@/components/ui/SMButton.vue';
import SMInput from '@/components/ui/SMInput.vue';
import SMNumberInput from '@/components/ui/SMNumberInput.vue';
import SMColorPicker from '@/components/ui/SMColorPicker.vue';
import SMSelect from '@/components/ui/SMSelect.vue';
import SMOption from '@/components/ui/SMOption.vue';
import { lastUsedSectionFontSize } from '@/core/utils';
import SMDialogConfirm from '@/components/ui/legacy/SMDialogConfirm.vue';

export default {
  components: {
    SMIcon,
    SMInput,
    SMNumberInput,
    SMColorPicker,
    SMButton,
    SMSelect,
    SMOption,
    SMDialogConfirm,
  },
  props: ['units'],
  data() {
    return {
      showLabelColorPicker: false,
      isGa: false,
      isNoGa: true,
      categoryId: this.units[0].categoryId,
      opacity: 100,
      fontColor: 'black',
      fontSize: lastUsedSectionFontSize.value,
      tempCategoryId: null,
    };
  },
  computed: {
    eventIsPublished() {
      // ga, category, sectionCode, split, duplicae, delete
      return this.$app.props.status === EVENTSTATUS.PUBLISHED;
    },
    hasServerSections() {
      return !!this.$app.props.sections;
    },
    category() {
      return this.multipleCategories ? '' : this.categoryId; // this.units[0].categoryId;
    },
    entrance: {
      get() {
        return this.multipleEntrances ? '' : this.units[0].attributes.entrance;
      },
      set(newValue) {
        this.$app.seatmap.triggerUnsavedChanges(true);

        this.units.forEach((unit) => {
          unit.attributes.entrance = newValue;
        });
      },
    },
    multipleCategories() {
      const categories = map(this.units, (unit) => unit.categoryId);
      const uniqueCategories = uniq(categories);
      return uniqueCategories.length > 1;
    },
    multipleEntrances() {
      const entrances = map(this.units, (unit) => unit.attributes.entrance);
      const uniqueEntrances = uniq(entrances);
      return uniqueEntrances.length > 1;
    },
    multipleLevels() {
      const levels = map(this.units, (unit) => unit.attributes.level);
      const uniqueLevels = uniq(levels);
      return uniqueLevels.length > 1;
    },
    categoryLabel() {
      return this.multipleCategories ? 'Multiple Categories assigned' : '';
    },
    entranceLabel() {
      return this.multipleEntrances ? 'Multiple Entrances assigned' : 'Entrance';
    },
    levelLabel() {
      return this.multipleLevels ? 'Multiple Levels assigned' : 'Level';
    },
    level: {
      get() {
        return this.multipleLevels ? '' : this.units[0].attributes.level;
      },
      set(newValue) {
        this.$app.seatmap.triggerUnsavedChanges(true);

        this.units.forEach((unit) => {
          unit.attributes.level = newValue;
        });
      },
    },
    draftEvent() {
      return this.$app.props.status === EVENTSTATUS.DRAFT;
    },
    pauseEvent() {
      return this.$app.props.status === EVENTSTATUS.PAUSED;
    },
    hasDrawingMode() {
      return this.$app.props.options.modes.map((s) => s.toLowerCase()).includes('drawing');
    },
    categories() {
      if (this.isGa) {
        const gaCategories = this.$store.state.categories.categories.filter((category) => category.type === 'ga');

        // used categories
        let usedCategories = [];

        if (this.$app.props.oneCatPerGa) {
          usedCategories = this.$app.seatmap.sections.map((section) => section.categoryId);
        }

        const filteredCategories = gaCategories.filter(
          (category) =>
            !usedCategories.includes(category.id) || this.units.map((u) => u.categoryId).includes(category.id)
        );

        if (filteredCategories.length > 0) {
          return filteredCategories.sort((a, b) => a.id - b.id);
        }

        return [
          {
            id: 0,
            name: 'No categories available',
          },
        ];

        // return this.$store.state.categories.categories.filter((category) => category.type === 'ga');
      } else if (this.isNoGa) {
        return this.$store.state.categories.categories
          .filter((category) => category.type === 'seated')
          .sort((a, b) => a.id - b.id);
      } else {
        return [...this.$store.state.categories.categories].sort((a, b) => a.id - b.id);
      }
    },
    visible: {
      get() {
        return this.units.some((unit) => unit.attributes.invisible !== true);
      },
      set(newValue) {
        this.units.forEach((unit) => {
          newValue ? unit.makeVisible() : unit.makeInvisible();
        });
        eventbus.emit('update:invisibleSeats');
        eventbus.emit('refreshUnassignedCount');
      },
    },
    ga: {
      get() {
        return this.units.some((unit) => unit.attributes.ga === true);
      },
      set(newValue) {
        this.isGa = newValue;
        this.isNoGa = !newValue;

        if (
          (this.$store.state.categories.categories.find((c) => c.id === this.categoryId)?.type === 'ga') !==
          newValue
        ) {
          this.categoryId = 0;
        }

        this.units.forEach((unit) => {
          unit.attributes.ga = newValue;
          if (unit.category && (unit.category.type === 'ga') !== newValue) {
            unit.assignCategory(0);
          }
        });
      },
    },
    hasUnits() {
      return this.units.some((unit) => this.$app.seatmap.unitsFromSection(unit.uuid).length > 0);
    },
    multipleLabelColors() {
      const uniqueColors = uniq(this.units.map((unit) => unit.attributes.label?.stroke));
      return uniqueColors.length > 1;
    },
    labelColor() {
      return this.multipleLabelColors ? '' : tinycolor(this.fontColor).toRgbString();
    },
    labelColorLabel() {
      return this.multipleLabelColors ? 'Multiple Colors assigned' : 'Label Color';
    },
    multipleFontSizes() {
      const uniqueFontSizes = uniq(this.units.map((unit) => unit.attributes.label?.size ?? TEXT.SIZE.SECTION));
      return uniqueFontSizes.length > 1;
    },
    fontSizeLabel() {
      return this.multipleFontSizes ? 'Multiple Font Sizes assigned' : 'Font Size';
    },
    multipleOpacities() {
      const uniqueOpacities = uniq(this.units.map((unit) => unit.attributes.opacity ?? 100));
      return uniqueOpacities.length > 1;
    },
    opacityLabel() {
      return this.multipleOpacities ? 'Multiple Opacities assigned' : 'Opacity';
    },
  },
  created() {
    this.isGa = this.units.every((u) => u.attributes.ga);
    this.isNoGa = this.units.every((u) => !u.attributes.ga);
    this.opacity = Math.min(...this.units.map((u) => u.attributes.opacity ?? 100));
    this.fontColor = this.units[0].attributes.label?.stroke ?? this.units[0].attributes.label?.fill ?? 'black';

    const firstSize = this.units[0].attributes.label?.size ?? TEXT.SIZE.SECTION;
    const avg =
      this.units.reduce((acc, unit) => acc + (unit.attributes.label?.size ?? TEXT.SIZE.SECTION), 0) / this.units.length;
    this.fontSize = this.multipleFontSizes ? avg : firstSize;
  },
  methods: {
    onCategorySelection(categoryId) {
      this.categoryId = categoryId;
      const category = this.$store.state.categories.categories.find((c) => c.id === categoryId);

      if (
        category &&
        category.type === 'ga' &&
        this.units.some((u) => this.$app.seatmap.seats.some((s) => s.sid === u.uuid))
      ) {
        this.tempCategoryId = categoryId;
        this.$refs.confirmCategoryAssignmentRef!.open();
        return;
      }

      this.$app.seatmap.triggerUnsavedChanges(true);
      this.units.forEach((unit) => {
        unit.attributes.ga = category?.type === 'ga';
        unit.assignCategory(categoryId);
      });
    },

    categoryAssignmentDialogClosed(value: 'ok' | 'cancel') {
      if (value === 'ok') {
        this.units.forEach((u) => {
          this.$app.seatmap.deleteSectionUnits(u.uuid);
        });
        this.onCategorySelection(this.tempCategoryId!);
      }
    },

    duplicate() {
      eventbus.emit('selection:clone');
    },

    remove() {
      eventbus.emit('selection:remove');
    },
    onOpacityChanged(value) {
      this.units.forEach((unit) => unit.changeOpacity(value));
    },

    onLabelColorChanged(color) {
      this.units.forEach((unit) => unit.changeLabelStroke(color));
    },

    onFontSizeValueChange(value) {
      lastUsedSectionFontSize.value = value;
      this.units.forEach((unit) => unit.changeFontSize(value));
    },
  },
};
</script>
<style lang="scss" scoped>
.md-small {
  width: auto;
  padding: 2px 3px;
  min-height: 20px;
  font-size: 0.9em;
  line-height: 2em;
}
.md-input-container {
  min-height: auto !important;
  margin: 0 !important;
  padding-top: 0;

  label {
    top: 10px;
  }
}
.md-input-focused {
  label {
    display: none;
  }
}
div {
  -webkit-user-select: none;
  user-select: none;
}
body {
  -moz-user-focus: ignore;
  -moz-user-select: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -o-user-select: none;
  user-select: none;
}
</style>
